/**
 * WidgetCard — one dashboard widget in a card, with move / remove controls in edit mode. Demo pages: /analytics and
 * /saas (chart and list cards with a ⋯ menu). Template example: NextAdmin `(dashboard)/(home)/_component/top-products`.
 * Used by the dashboard editor and the simple view. Removing a widget is gated by the caller.
 */
import React, { Suspense, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { ChartCard } from '../../components/charts/ChartCard.js';
import { LineChart } from '../../components/charts/LineChart.js';
import { OutputRenderer, type RenderableOutput } from '../../components/dashboard/OutputRenderer.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { getOutputMetricsOptions, queryOutputsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { DashboardWidget, GetOutputMetricsData, QueryOutputsData } from '../../generated/api/types.gen.js';
import { widgetDataPlan } from '@confluent/shared/lib/dashboard-widget-data';

// QG-CLI-PAGES-005: finance-domain widgets must not be statically imported by
// core — that would ship finance code into every org's bundle regardless of
// module enablement. Lazy-load it instead; only orgs that ever render a
// `finance_ops_section` widget (which the finance module itself contributes,
// per `organization_modules`) pull the chunk.
const FinanceOpsSectionWidget = React.lazy(() =>
  import('../../../../modules/finance/client/widgets/FinanceOpsSectionWidget.js').then((m) => ({
    default: m.FinanceOpsSectionWidget,
  })),
);

// ==========================================
// Widget Data Fetcher
// ==========================================

/**
 * The widget's own settings layered over its catalog defaults. A widget added from the conversation
 * stores only what was said about it (e.g. `{ title }`); taking `config` INSTEAD of the defaults lost
 * `dataSource`/`kind` and left every such widget empty.
 */
export function effectiveWidgetConfig(widget: DashboardWidget): Record<string, unknown> {
  return {
    ...((widget.definition?.defaultConfig as Record<string, unknown> | undefined) ?? {}),
    ...((widget.config as Record<string, unknown> | null) ?? {}),
  };
}

/** What a widget fetches: its feed of output parts, or a metric's time series. */
export type WidgetDataRequest =
  | { source: 'output'; key: readonly unknown[]; query: (now: number) => NonNullable<QueryOutputsData['query']> }
  | { source: 'metric'; key: readonly unknown[]; query: (now: number) => NonNullable<GetOutputMetricsData['query']> };

/**
 * What a widget fetches. The key names the request by its configuration only; the query is built at fetch time. A
 * metric window's `from` used to be computed from the clock on every render and sat inside the query key, so every
 * render started a new query: a metric widget refetched in a loop and never left its spinner. What a widget reads is
 * the shared plan (`@confluent/shared/lib/dashboard-widget-data`), the one `dashboard_read { data: true }` reads too.
 */
export function widgetDataRequest(widget: DashboardWidget): WidgetDataRequest | null {
  const plan = widgetDataPlan(effectiveWidgetConfig(widget));
  if (!plan) return null;
  if (plan.source === 'output') {
    // Optional scope to one workflow (e.g. the content monitor's reports) — otherwise the org feed.
    const query = {
      kind: plan.kind,
      ...(plan.partTitle ? { title: plan.partTitle } : {}),
      visibility: 'dashboard',
      limit: String(plan.limit),
      ...(plan.workflowId ? { workflowId: plan.workflowId } : {}),
    };
    return { source: 'output', key: [plan.source, query], query: () => query };
  }
  return {
    source: 'metric',
    key: [plan.source, plan.workflowId, plan.metricName, plan.rangeMs, plan.limit],
    query: (now) => ({
      workflowId: plan.workflowId,
      metricName: plan.metricName,
      from: new Date(now - plan.rangeMs).toISOString(),
      limit: String(plan.limit),
    }),
  };
}

const REFRESH_MS = 30_000;
const MINUTE_MS = 60_000;

/** The clock in whole minutes: a metric window slides once a minute, so a render never starts a new query. */
function useMinuteClock(): number {
  const [now, setNow] = useState(() => Math.floor(Date.now() / MINUTE_MS) * MINUTE_MS);
  useEffect(() => {
    const timer = setInterval(() => setNow(Math.floor(Date.now() / MINUTE_MS) * MINUTE_MS), MINUTE_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** The widget's data, or null when it reads nothing. Each branch is a generated query and its own typed answer. */
function useWidgetData(widget: DashboardWidget) {
  const request = widgetDataRequest(widget);
  const now = useMinuteClock();
  const output = useQuery({
    ...queryOutputsOptions({ query: request?.source === 'output' ? request.query(now) : {} }),
    enabled: request?.source === 'output',
    refetchInterval: REFRESH_MS,
  });
  const metric = useQuery({
    ...getOutputMetricsOptions({ query: request?.source === 'metric' ? request.query(now) : {} }),
    enabled: request?.source === 'metric',
    refetchInterval: REFRESH_MS,
  });
  if (request?.source === 'output') {
    return {
      isLoading: output.isPending,
      isError: output.isError,
      data: output.data ? ({ source: 'output', parts: output.data.data } as const) : null,
    };
  }
  if (request?.source === 'metric') {
    return {
      isLoading: metric.isPending,
      isError: metric.isError,
      data: metric.data ? ({ source: 'metric', points: metric.data.data } as const) : null,
    };
  }
  return { isLoading: false, isError: false, data: null };
}

// ==========================================
// Single Widget Card
// ==========================================

export interface WidgetCardProps {
  widget: DashboardWidget;
  editMode: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

const WidgetSkeleton: React.FC = () => (
  <Card className="space-y-3" aria-hidden="true">
    <Skeleton height={14} width="40%" />
    <Skeleton height={72} rounded="rounded-lg" />
  </Card>
);

export const WidgetCard: React.FC<WidgetCardProps> = ({
  widget,
  editMode,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onRemove,
}) => {
  const { data, isLoading, isError } = useWidgetData(widget);

  // System (H1) widget type — self-fetches the finance-operations-summary read model.
  const isFinanceOpsSection = widget.definition?.type === 'finance_ops_section';

  // Build a synthetic output for OutputRenderer
  const config = effectiveWidgetConfig(widget);

  // The title the widget was given wins over the catalog type's name and over the part's own title: "Sector Rankings
  // Table" (the table type's catalog name) and "View Summary" (a part's title) read as another widget's.
  const ownTitle = typeof config.title === 'string' && config.title.trim().length > 0 ? config.title.trim() : null;

  let outputForRenderer: RenderableOutput | null = null;

  if (data?.source === 'output' && data.parts.length > 0) {
    const part = data.parts[0];
    outputForRenderer = ownTitle ? { ...part, title: ownTitle } : part;
  }

  const name = ownTitle ?? widget.definition?.name ?? 'Widget';
  // A metric's series over time, oldest first, as a line (a view's composite runs from −1 to 1, so never bars).
  const metricRows =
    data?.source === 'metric' && data.points.length > 0
      ? [...data.points]
          .sort((a, b) => Date.parse(a.time) - Date.parse(b.time))
          .map((m) => ({ time: new Date(m.time).toISOString().slice(0, 16).replace('T', ' '), value: Number(m.value) }))
      : null;

  return (
    <div className="relative">
      {editMode && (
        <div className="absolute -top-3 -right-2 z-10 flex items-center gap-1 rounded-lg border border-card-border bg-surface p-1 shadow-sm">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Move ${name} up`}
            disabled={isFirst}
            onClick={onMoveUp}
          >
            <ChevronUp size={14} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Move ${name} down`}
            disabled={isLast}
            onClick={onMoveDown}
          >
            <ChevronDown size={14} aria-hidden="true" />
          </Button>
          <Button variant="ghost" size="sm" iconOnly aria-label={`Remove ${name}`} onClick={onRemove}>
            <Trash2 size={14} className="text-danger" aria-hidden="true" />
          </Button>
        </div>
      )}

      {isFinanceOpsSection && (
        <Suspense fallback={<WidgetSkeleton />}>
          <FinanceOpsSectionWidget widget={widget} />
        </Suspense>
      )}

      {!isFinanceOpsSection && isLoading && <WidgetSkeleton />}

      {!isFinanceOpsSection && !isLoading && isError && (
        <Card>
          <h4 className="mb-1 text-sm font-semibold text-heading">{name}</h4>
          <p className="text-xs text-danger">Could not load data for this widget.</p>
        </Card>
      )}

      {!isFinanceOpsSection && !isLoading && !isError && metricRows && (
        <ChartCard title={name}>
          <LineChart
            data={metricRows}
            xKey="time"
            series={[{ key: 'value', label: typeof config.metricName === 'string' ? config.metricName : 'Value' }]}
            title={name}
            summary={`${metricRows.length} points, latest ${metricRows.at(-1)?.value.toFixed(3) ?? 'none'}`}
            height={200}
            formatValue={(value) => value.toFixed(3)}
            formatX={(value) => value.slice(5, 10)}
          />
        </ChartCard>
      )}

      {!isFinanceOpsSection && !isLoading && !isError && outputForRenderer && (
        <>
          {/* The table renderer draws no title of its own; on a dashboard the widget's name says what it is. */}
          {outputForRenderer.kind === 'table' && (
            <h4 className="mb-2 px-1 text-sm font-semibold text-heading">{outputForRenderer.title}</h4>
          )}
          <OutputRenderer output={outputForRenderer} />
        </>
      )}

      {!isFinanceOpsSection && !isLoading && !isError && !outputForRenderer && !metricRows && (
        <Card>
          <h4 className="mb-1 text-sm font-semibold text-heading">{name}</h4>
          <p className="text-xs text-muted">
            {data !== null
              ? 'No data yet. It fills in when its workflow next produces this output.'
              : (widget.definition?.description ?? 'No data yet. Run a workflow to fill this widget.')}
          </p>
        </Card>
      )}
    </div>
  );
};
